import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import extractValue, {
  type EventOrValue,
} from '../../../../store/utils/extractValue';
import Validator from '../../../../store/utils/Validator';
import { toError } from '../../../../utils/errors';

export default class SigningIn {
  static Schema = z.object({
    email: z.email(),
    password: z.string().min(1),
  });

  private validator = new Validator(this, SigningIn.Schema);

  email = '';
  password = '';

  isProcessing = false;
  error?: Error;

  constructor(
    private readonly onSignIn: (
      email: string,
      password: string,
    ) => Promise<void>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get errors(): Partial<Record<'email' | 'password' | 'response', string>> {
    return {
      ...this.validator.errorsForEnabledFields,
      response: this.error?.message,
    };
  }

  async signIn(): Promise<boolean> {
    this.validator.enableAllFieldValidation();
    this.validator.validate();
    if (!this.validator.isValid) {
      return false;
    }

    this.updateState(true);

    try {
      await this.onSignIn(this.email.trim().toLowerCase(), this.password);
      this.updateState(false);
      return true;
    } catch (e) {
      this.updateState(false, toError(e));
      return false;
    }
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);

    Object.assign(this, { [name]: value });
    this.error = undefined;

    this.validator.validate();
  }

  handleBlur(e: EventOrValue<keyof SigningIn>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  private updateState(isProcessing: boolean, error?: Error) {
    this.isProcessing = isProcessing;
    this.error = error;
  }
}
